import { useCallback } from 'react';

import type { UUID } from '@aline/common';
import type { ApolloLink } from '@apollo/client';
import { gql } from '@apollo/client';
import { Apollo } from 'src/gql/generated';

const columnTypename = 'Column' as const;

const EMPTY_BOARD_COLUMNS: Apollo.ColumnBaseFragment[] = [];

/**
 * Column options for a board, without per-column workflows. Backs pickers like the
 * phase home-column chip; `refetch` lets the picker refresh options when it opens.
 */
export function useBoardColumns(boardId: UUID): {
  boardColumns: Apollo.ColumnBaseFragment[];
  loading: boolean;
  refetchBoardColumns: () => void;
  signingColumnId: UUID | null;
} {
  const { data, loading, refetch } = Apollo.useGetBoardColumnsQuery({ variables: { boardId } });
  const refetchBoardColumns = useCallback(() => {
    void refetch();
  }, [refetch]);

  return {
    boardColumns: data?.board.columns ?? EMPTY_BOARD_COLUMNS,
    loading,
    refetchBoardColumns,
    signingColumnId: data?.board.signingColumn?.id ?? null,
  };
}

/**
 * Hook for creating a new board with cache update.
 * Automatically adds the new board to the GetBoardsQuery cache.
 */
export function useCreateBoard(): {
  createBoard: (
    input: Apollo.CreateBoardInput
  ) => Promise<Apollo.CreateBoardMutation['createBoard'] | null>;
  loading: boolean;
} {
  const [createBoardMutation, { loading }] = Apollo.useCreateBoardMutation();

  const createBoard = useCallback(
    async (input: Apollo.CreateBoardInput) => {
      const result = await createBoardMutation({
        update(cache, { data }) {
          const newBoard = data?.createBoard;
          if (!newBoard) return;

          // Write the new board to the cache
          const newBoardRef = cache.writeFragment({
            data: newBoard,
            fragment: gql`
              fragment NewBoard on Board {
                __typename
                id
              }
            `,
          });

          if (!newBoardRef) return;

          // Add the new board to the boards list
          cache.modify({
            fields: {
              boards(existingBoardRefs = [], { readField }) {
                // Check if board already exists to avoid duplicates
                const exists = existingBoardRefs.some(
                  (ref: { __ref: string }) => readField('id', ref) === newBoard.id
                );
                if (exists) return existingBoardRefs;

                return [...existingBoardRefs, newBoardRef];
              },
            },
          });
        },
        variables: { input },
      });

      return result.data?.createBoard ?? null;
    },
    [createBoardMutation]
  );

  return { createBoard, loading };
}

/**
 * Hook for creating a new column with cache update.
 * Automatically adds the new column to the board's columns list.
 */
export function useCreateColumn(boardId: UUID): {
  createColumn: (
    input: Omit<Apollo.CreateColumnInput, 'boardId'>
  ) => Promise<Apollo.CreateColumnMutation['createColumn'] | null>;
  loading: boolean;
} {
  const [createColumnMutation, { loading }] = Apollo.useCreateColumnMutation();

  const createColumn = useCallback(
    async (input: Omit<Apollo.CreateColumnInput, 'boardId'>) => {
      const result = await createColumnMutation({
        update(cache, { data }) {
          const newColumn = data?.createColumn;
          if (!newColumn) return;

          // Write complete column data so GetBoardQuery cache read stays intact
          const newColumnRef = cache.writeFragment({
            data: { ...newColumn, workflows: [] },
            fragment: Apollo.ColumnWithWorkflowsFragmentDoc,
            fragmentName: 'ColumnWithWorkflows',
          });

          if (!newColumnRef) return;

          // Add the new column to the board's columns list
          cache.modify({
            fields: {
              columns(existingColumnRefs = [], { readField }) {
                const exists = existingColumnRefs.some(
                  (ref: { __ref: string }) => readField('id', ref) === newColumn.id
                );
                if (exists) return existingColumnRefs;

                return [...existingColumnRefs, newColumnRef];
              },
            },
            id: cache.identify({ __typename: 'Board', id: boardId }),
          });
        },
        variables: { input: { ...input, boardId } },
      });

      return result.data?.createColumn ?? null;
    },
    [boardId, createColumnMutation]
  );

  return { createColumn, loading };
}

/**
 * Hook for updating a column.
 * Apollo automatically updates the cache from the mutation response.
 */
export function useUpdateColumn(): {
  loading: boolean;
  updateColumn: (
    columnId: UUID,
    props: Apollo.UpdateColumnProps
  ) => Promise<ApolloLink.Result<Apollo.UpdateColumnMutation>>;
} {
  const [updateColumnMutation, { loading }] = Apollo.useUpdateColumnMutation();

  const updateColumn = useCallback(
    (columnId: UUID, props: Apollo.UpdateColumnProps) => {
      return updateColumnMutation({
        variables: { input: { columnId, props } },
      });
    },
    [updateColumnMutation]
  );

  return { loading, updateColumn };
}

/**
 * Hook for deleting a column with cache eviction.
 * Workflows and phase homes referencing the column move to `targetColumnId`.
 */
export function useDeleteColumn(): {
  deleteColumn: (
    columnId: UUID,
    targetColumnId: UUID
  ) => Promise<ApolloLink.Result<Apollo.DeleteColumnMutation>>;
  loading: boolean;
} {
  const [deleteColumnMutation, { loading }] = Apollo.useDeleteColumnMutation();

  const deleteColumn = useCallback(
    (columnId: UUID, targetColumnId: UUID) => {
      return deleteColumnMutation({
        update(cache, { data }) {
          if (data?.deleteColumn) {
            cache.evict({
              id: cache.identify({ __typename: columnTypename, id: data.deleteColumn }),
            });
            cache.gc();
          }
        },
        variables: { columnId, targetColumnId },
      });
    },
    [deleteColumnMutation]
  );

  return { deleteColumn, loading };
}

/**
 * Hook for moving a single column before another column.
 * Apollo automatically updates the cache from the mutation response.
 */
export function useMoveColumn(): {
  loading: boolean;
  moveColumn: (
    columnId: UUID,
    targetColumnId: UUID
  ) => Promise<ApolloLink.Result<Apollo.MoveColumnMutation>>;
} {
  const [moveColumnMutation, { loading }] = Apollo.useMoveColumnMutation();

  const moveColumn = useCallback(
    (columnId: UUID, targetColumnId: UUID) => {
      return moveColumnMutation({
        variables: { input: { columnId, targetColumnId } },
      });
    },
    [moveColumnMutation]
  );

  return { loading, moveColumn };
}

/**
 * Hook for adding the triage column to a board. Removing it again is `useDeleteColumn`.
 * Apollo automatically updates the cache from the mutation response (returns full Board).
 */
export function useAddTriageColumn(boardId: UUID): {
  addTriageColumn: () => Promise<ApolloLink.Result<Apollo.AddTriageColumnMutation>>;
  loading: boolean;
} {
  const [addTriageColumnMutation, { loading }] = Apollo.useAddTriageColumnMutation();
  const addTriageColumn = useCallback(() => {
    return addTriageColumnMutation({ variables: { boardId } });
  }, [addTriageColumnMutation, boardId]);

  return { addTriageColumn, loading };
}

/**
 * Hook for updating a board's name, icon, and color.
 * Apollo automatically updates the cache from the mutation response.
 */
export function useUpdateBoard(): {
  loading: boolean;
  updateBoard: (
    boardId: UUID,
    props: Apollo.UpdateBoardProps
  ) => Promise<ApolloLink.Result<Apollo.UpdateBoardMutation>>;
} {
  const [updateBoardMutation, { loading }] = Apollo.useUpdateBoardMutation();

  const updateBoard = useCallback(
    (boardId: UUID, props: Apollo.UpdateBoardProps) => {
      return updateBoardMutation({
        variables: { input: { boardId, props } },
      });
    },
    [updateBoardMutation]
  );

  return { loading, updateBoard };
}
